import { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import BreastfeedingForm from "../../components/forms/breastfeedingForm";
import BreastfeedingTable from "../../components/tables/breastfeedingTable";
import LoadingState from "../../components/ui/LoadingState";
import EmptyState from "../../components/ui/EmptyState";
import SectionSummaryBar from "../../components/ui/SectionSummaryBar";
import { useFirestore } from "../../hooks/useFirestore";
import { useChild } from "../../contexts/ChildContext";
import { useTimer } from "../../contexts/TimerContext";
import { toDatetimeLocal, parseItemDate, formatTimeAgo } from "../../utils/dateUtils";
import { exportToCSV, breastfeedingCSVColumns } from "../../utils/exportUtils";
import { exportSectionPDF } from "../../utils/pdfUtils";

function timerToFeedValues(timerData) {
  if (!timerData) return null;
  const start = new Date(timerData.startTime);
  return {
    date:     toDatetimeLocal(start),
    duration: Math.max(1, Math.round(timerData.elapsedSeconds / 60)),
  };
}

function formatDuration(seconds) {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

function formatMins(min) {
  if (!min) return '0m';
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
}

const Breastfeeding = () => {
  const { selectedChild } = useChild();
  const { items: feedData, addItem, updateItem, deleteItem, loading, error } =
    useFirestore("breastfeeding", selectedChild?.id);
  const { timer, elapsed, start: startTimer } = useTimer();
  const location = useLocation();

  const timerData     = location.state?.timerData?.type === 'breastfeeding' ? location.state.timerData : null;
  const initialValues = timerToFeedValues(timerData);
  const [noticeDismissed, setNoticeDismissed] = useState(false);

  // Today at a glance + last side
  const bfStats = useMemo(() => {
    const todayStr = new Date().toDateString();
    const sorted = [...feedData]
      .map(f => ({ item: f, date: parseItemDate(f) }))
      .filter(x => x.date)
      .sort((a, b) => b.date - a.date);

    const todayFeeds = sorted.filter(x => x.date.toDateString() === todayStr);
    const count = todayFeeds.length;
    const totalMin = todayFeeds.reduce((s, x) => s + parseFloat(x.item.duration || 0), 0);

    const lastFeed = sorted[0]?.item ?? null;
    const lastSide = lastFeed?.side ?? null;
    const nextSide = lastSide === 'left' ? 'right' : lastSide === 'right' ? 'left' : null;
    const lastFeedDate = sorted[0]?.date ?? null;

    return { count, totalMin, lastSide, nextSide, lastFeedDate };
  }, [feedData]);

  const summaryStats = useMemo(() => {
    if (!feedData.length) return null;
    return [
      { value: String(bfStats.count), label: bfStats.count === 1 ? 'session today' : 'sessions today' },
      { value: formatMins(bfStats.totalMin), label: 'today total' },
      { value: formatTimeAgo(bfStats.lastFeedDate) ?? '—', label: 'last fed' },
    ];
  }, [feedData, bfStats]);

  const handleAdd = async (feed) => {
    try {
      await addItem({ ...feed, childId: selectedChild?.id });
    } catch (err) {
      console.error("Error adding breastfeeding entry:", err);
    }
  };

  const handleExport = () =>
    exportToCSV(feedData, `breastfeeding-${selectedChild?.name || "export"}.csv`, breastfeedingCSVColumns);

  const handleExportPDF = () =>
    exportSectionPDF(feedData, `breastfeeding-${selectedChild?.name || "export"}.pdf`, breastfeedingCSVColumns, "Breastfeeding", selectedChild?.name || "");

  const thisFeedTimerRunning = timer?.type === 'breastfeeding';

  return (
    <div className="card h-100" id="breastfeeding">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">🤱 Breastfeeding</h5>
          <div className="d-flex gap-1 align-items-center flex-wrap">
            {thisFeedTimerRunning ? (
              <span className="timer-running-badge">
                <span style={{ animation: 'timer-dot-pulse 1.4s ease-in-out infinite', display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--primary-dark)', marginRight: 2 }} aria-hidden="true" />
                {Math.floor(elapsed / 60)}m running
              </span>
            ) : !timer ? (
              <button
                className="timer-start-btn"
                onClick={() => startTimer('breastfeeding')}
                title="Start a live breastfeeding timer"
              >
                ▶ Start Timer
              </button>
            ) : null}
            <button className="btn btn-sm btn-outline-secondary" onClick={handleExport}>⬇ CSV</button>
            <button className="btn btn-sm btn-outline-primary" onClick={handleExportPDF}>⬇ PDF</button>
          </div>
        </div>

        {/* Last side indicator — the #1 thing nursing parents need at a glance */}
        {!loading && bfStats.lastSide && bfStats.nextSide && (
          <div className="bf-last-side-bar">
            <span className="bf-last">Last: <strong>{bfStats.lastSide.toUpperCase()}</strong></span>
            <span className="bf-arrow">→</span>
            <span className="bf-next">Start next on: <strong>{bfStats.nextSide.toUpperCase()}</strong> breast</span>
          </div>
        )}

        {!loading && summaryStats && <SectionSummaryBar stats={summaryStats} />}

        {timerData && !noticeDismissed && (
          <div className="timer-prefill-notice">
            <span>⏱</span>
            <span>Form pre-filled from timer — <strong>{formatDuration(timerData.elapsedSeconds)}</strong> session</span>
            <button
              className="timer-prefill-dismiss"
              onClick={() => setNoticeDismissed(true)}
              aria-label="Dismiss notice"
            >✕</button>
          </div>
        )}

        {error && <div className="alert alert-danger">{error}</div>}
        <BreastfeedingForm onAdd={handleAdd} initialValues={initialValues} />
        {loading ? (
          <LoadingState message="Loading breastfeeding data..." />
        ) : feedData.length > 0 ? (
          <BreastfeedingTable data={feedData} onUpdate={updateItem} onDelete={deleteItem} />
        ) : (
          <EmptyState
            icon="🤱"
            heading="No sessions logged yet"
            message="Record your first session to start tracking feeds and timing."
          />
        )}
      </div>
    </div>
  );
};

export default Breastfeeding;
